import { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Text } from '@comicrelief/component-library';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import RouteProps from '@/types/shared-types';
import { useCartContext } from '@/services/cart/CartContext';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import { useValidateTransactionPending } from '@/features/paymentProcess/useValidateTransactionPending';
import { useValidatePaymentSelectState } from '@/features/paymentSelect/useValidatePaymentSelectState';
import { clearErrorMessage } from '@/features/paymentProcess/transactionSlice';
import { PostcodeLookupAddress } from '@/api/lookups/postcodeLookup';
import { usePaymentsConfig } from '@/services/paymentsConfig/usePaymentsConfig';
import { getPageHeaderInitialText, getPageHeaderSupplementaryText } from '@/utils/paymentTextUtils';

import PageHeader from '@/components/pageHeader/PageHeader';
import PaymentErrorText from '@/features/paymentProcess/PaymentErrorText';
import StepBack from '@/components/stepBack/StepBack';
import PostcodeLookup from '@/components/postcodeLookup/PostcodeLookup';
import CheckboxWithSvg from '@/components/common/CheckboxWithSvg';
import PrivacyNotice from '@/components/privacyNotice/PrivacyNotice';
import {
  StyledButtonWithStates,
  FullWidthStyledInput,
  StyledSelect,
  HorizontalLine,
  PostcodeSearchWrapper,
  ButtonWrapper,
  ReviewDetailsWrapper,
  AddressField,
  AddressFieldsWrapper,
  UseOtherAddressWrapper,
  EditDetailsButton
} from '../../components/global/Global.style';

import { updateShippingDetailsFields, setUseBillingAddress } from './shippingDetailsSlice';
import { formFields, getFormResolver, ShippingFormData } from './ShippingDetailsFields';
import { mapLookupToAddress } from '@/utils/addressMappingUtils';
import { logger } from '@/services/logger/logger';

import { FULFILMENT_TYPES } from '@/constants/constants';
import { countriesOptions, DEFAULT_COUNTRY } from '@/locale';

const {
  childName,
  country,
} = formFields;

function ShippingDetails({ totalSteps, currencySymbol }: RouteProps) {
  const dispatch = useAppDispatch();
  const userDetailsState = useAppSelector(state => state.userDetails);
  const journeyNavigate = useJourneyNavigate();
  const shippingDetailsState = useAppSelector(state => state.shippingDetails);
  const { postalRewards } = shippingDetailsState;

  // Validate state is as expected, otherwise redirect to index or a previous step
  useValidateTransactionPending();
  useValidatePaymentSelectState();

  const { isDependencyEnabled } = usePaymentsConfig();
  const lookupsEnabled = isDependencyEnabled('plus');

  const { getCartFulfilmentType } = useCartContext();
  const isAARN = getCartFulfilmentType() === FULFILMENT_TYPES.AARN;

  // For use in journeys with postal items we don't want to send overseas.
  const isUKOnlyPostage = isAARN;
  // Get the default country (UK)
  const getDefaultCountry = () => countriesOptions.filter(option => option.value === DEFAULT_COUNTRY);

  const countriesList = isUKOnlyPostage ? getDefaultCountry() : country.options;

  const formMethods = useForm<ShippingFormData>({
    mode: 'onBlur',
    resolver: getFormResolver(),
    defaultValues: {
      childName: shippingDetailsState.childName,
      country: isUKOnlyPostage ? DEFAULT_COUNTRY : shippingDetailsState.country,
      address1: shippingDetailsState.address1,
      address2: shippingDetailsState.address2,
      address3: shippingDetailsState.address3,
      town: shippingDetailsState.town,
      postcode: shippingDetailsState.postcode,
    },
  });

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    formState: { errors: formErrors },
  } = formMethods;

  useEffect(() => {
    const watchInputs = watch((formValues, { name }) => {
      if (!name) {
        return;
      }
      // Update shipping details state based on field name
      if (typeof name === 'string' && name in formValues) {
        const key = name as keyof ShippingFormData;
        dispatch(
          updateShippingDetailsFields({ [key]: formValues[key] } as Partial<ShippingFormData>)
        );
      } else {
        logger.warn(`Unexpected field name in shipping details form: ${String(name)}`);
      }
    });
    return () => watchInputs.unsubscribe();
  }, [watch, dispatch]);

  const onSubmit = () => {
    try {
      dispatch(clearErrorMessage());
      journeyNavigate('next');
    } catch (error) {
      logger.error('Error during form submission', error);
    }
  };

  const onPostcodeLookupSelect = (result: PostcodeLookupAddress) => {
    try {
      const addressMapping = mapLookupToAddress(result);

      // Update form fields
      Object.entries(addressMapping).forEach(([field, value]) => {
        setValue(field as keyof ShippingFormData, value as string);
      });

      // Update shipping details state
      dispatch(updateShippingDetailsFields(addressMapping));

      void formMethods.trigger();
    } catch (error) {
      logger.error(error instanceof Error ? error.message : 'Error mapping postcode lookup result');
    }
  };

  const handleUseBillingAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const useBillingAddress = event.target.checked;
    dispatch(setUseBillingAddress(useBillingAddress));

    if (useBillingAddress) {
      // Copy billing address to shipping address
      const billingAddressMapping = {
        address1: userDetailsState.address1,
        address2: userDetailsState.address2,
        address3: userDetailsState.address3,
        town: userDetailsState.town,
        postcode: userDetailsState.postcode,
        country: userDetailsState.country,
      };

      dispatch(updateShippingDetailsFields(billingAddressMapping));

      // Update form fields
      Object.entries(billingAddressMapping).forEach(([field, value]) => {
        setValue(field as keyof ShippingFormData, value);
      });
    }
  };

  const handleEditBillingAddress = () => {
    // Navigate back to the previous page (user-details)
    journeyNavigate('back');
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={event => void handleSubmit(onSubmit)(event)} noValidate>
          <PageHeader
            currentStep={4}
            totalSteps={totalSteps}
            headerText="Shipping details"
            // Setting the majority of these flags by hand as this step is ONLY shown on a monthly AARN journey:
            initialText={getPageHeaderInitialText({ isPayIn: false, providerGroup: 'monthly', isAARN })}
            supplementaryText={getPageHeaderSupplementaryText({ isAARN, postalRewards })}
            currencySymbol={currencySymbol}
          />

          <PaymentErrorText />

          <Text
            tag="h4"
            family="Montserrat"
          >
            Addressee details
          </Text>

          <Text
            tag="p"
            family="Montserrat"
            size="s"
            margin="m"
          >
            We know kids find it very exciting to receive mail. Why not
            {' '}
            <b>enter their name</b>
            , and we&#39;ll address the quarterly printed activity packs and plushie toy to them

          </Text>

          <FullWidthStyledInput
            label={childName.label}
            id={childName.id}
            type={childName.type}
            errorMsg={formErrors?.childName?.message}
            {...register('childName')}
          />

          <HorizontalLine $margin="l" />

          <Text
            tag="h4"
            family="Montserrat"
            color="black"
            data-test="shipping-address-heading"
          >
            Shipping address
            {isUKOnlyPostage && ' (UK only)'}
          </Text>

          <UseOtherAddressWrapper>
            <CheckboxWithSvg
              checked={shippingDetailsState.useBillingAddress}
              htmlFor="useBillingAddress"
              dataTest="useBillingAddress"
              svgFilePath="/assets/images/location.svg"
              onChange={handleUseBillingAddressChange}
              hiddenCheckboxId="useBillingAddress"
            >
              Use Billing Address
            </CheckboxWithSvg>
          </UseOtherAddressWrapper>

          {shippingDetailsState.useBillingAddress ? (
            <ReviewDetailsWrapper data-test="reviewBillingAddress">
              <AddressField>{userDetailsState.address1}</AddressField>
              {userDetailsState.address2 && <AddressField>{userDetailsState.address2}</AddressField>}
              {userDetailsState.address3 && <AddressField>{userDetailsState.address3}</AddressField>}
              <AddressField>{userDetailsState.town}</AddressField>
              <AddressField>{userDetailsState.postcode}</AddressField>

              <EditDetailsButton
                type="button"
                color="white"
                onClick={handleEditBillingAddress}
                data-test="editBillingAddress"
                fullWidth
              >
                Edit billing address
              </EditDetailsButton>
            </ReviewDetailsWrapper>
          ) : (
            <>
              <StyledSelect
                label={country.label}
                id={country.id}
                options={countriesList}
                errorMsg={formErrors?.country?.message}
                optional={false}
                {...register('country')}
                hideArrow
              />

              {(shippingDetailsState.country === 'GB' && lookupsEnabled) && (
                <PostcodeSearchWrapper>
                  <PostcodeLookup onSelect={onPostcodeLookupSelect} />
                </PostcodeSearchWrapper>
              )}

              <HorizontalLine $margin="m" />

              <Text tag="h4" family="Montserrat" color="black">
                Or enter your shipping address manually
              </Text>

              <AddressFieldsWrapper data-test="shippingAddressDisplay">
                {(['address1', 'address2', 'address3', 'town', 'postcode'] as const).map((fieldName) => {
                  const field = formFields[fieldName];

                  return (
                    <FullWidthStyledInput
                      key={fieldName}
                      label={field.label}
                      id={field.id}
                      errorMsg={formErrors?.[fieldName]?.message}
                      type={field.type}
                      optional={fieldName === 'address2' || fieldName === 'address3'}
                      {...register(fieldName)}
                    />
                  );
                })}
              </AddressFieldsWrapper>
            </>
          )}

          <PrivacyNotice />

          <ButtonWrapper>
            <StyledButtonWithStates
              type="submit"
              fullWidth
              data-test="shippingDetailsContinue"
            >
              Continue
            </StyledButtonWithStates>
          </ButtonWrapper>
        </form>
      </FormProvider>
      <StepBack testId="shippingDetailsGoBack" />
    </>
  );
}

export default ShippingDetails;
